import { useMemo } from 'react';
import { Database, GitBranch, Lock, Server } from 'lucide-react';

function PublishedCard({ entry }) {
  const ok = entry.status === 'published';
  return (
    <div className="rounded-lg border border-cyan-500/20 bg-cyan-950/10 px-3 py-2">
      <p className="text-sm font-medium text-cyan-100">
        <span className="font-mono text-[11px] text-cyan-400/90">{entry.artifact_id}</span>
        {' · '}
        {entry.name || 'Artifact'}
      </p>
      <p className="text-[10px] text-slate-500 mt-0.5">
        {entry.artifact_type}
        {' · '}
        v{entry.current_version}
        {' · '}
        {entry.node_count ?? 0} nodes
        {' · '}
        {entry.edge_count ?? 0} edges
        {entry.slide_id ? ` · ${entry.slide_id}` : ''}
      </p>
      {!ok ? (
        <p className="text-[10px] text-red-400/90 mt-1">{entry.error || 'Failed'}</p>
      ) : null}
    </div>
  );
}

export function ArtifactAuthoringV3Panel({ data }) {
  const published = data?.published_artifacts || [];
  const endpoints = data?.endpoints || {};

  const endpointList = useMemo(
    () => Object.entries(endpoints).map(([k, v]) => ({ key: k, path: v })),
    [endpoints],
  );

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-cyan-500/25 bg-cyan-950/15 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm text-cyan-200">
          <Server className="w-4 h-4" />
          {data.total_published} artifacts published to Authoring API
          {data.step === '5C.2' ? (
            <span className="text-[10px] text-cyan-400/70">(Step 5C.2)</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Database className="w-4 h-4" />
          EDM source of truth — versioned, no SVG/PNG stored
        </div>
        {data.optimistic_locking ? (
          <span className="text-[10px] text-amber-400/80 flex items-center gap-1 ml-auto">
            <Lock className="w-3 h-3" />
            Optimistic locking enabled
          </span>
        ) : null}
      </div>

      <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-3">
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">Authoring flow</p>
        <div className="text-[11px] text-slate-400 font-mono space-y-1">
          <p>5C.1 EDM → <span className="text-cyan-300">POST /flows/v3/artifact-authoring</span> → persist artifacts + v1</p>
          <p>Edit → <span className="text-cyan-300">PUT/POST /edm/artifacts/…</span> → new version (expected_version)</p>
          <p>Conflict → <span className="text-amber-300">409</span> with current_version</p>
          <p>Restore → <span className="text-cyan-300">POST …/versions/N/restore</span> → new version (history preserved)</p>
        </div>
      </div>

      {endpointList.length ? (
        <div>
          <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
            <GitBranch className="w-3.5 h-3.5" />
            API base: {data.api_base || '/api/learning-tutorials/edm'}
          </p>
          <ul className="grid sm:grid-cols-2 gap-1 text-[10px] font-mono text-slate-400">
            {endpointList.map(({ key, path }) => (
              <li key={key} className="px-2 py-1 rounded bg-slate-950/60 border border-slate-800">
                <span className="text-cyan-500/80">{key}</span>
                {' '}
                {path}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {published.length ? (
        <div className="space-y-2">
          <p className="text-xs text-slate-500">Published artifacts</p>
          {published.map((e) => (
            <PublishedCard key={e.artifact_id || e.name} entry={e} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
